import {CheckCircle2Icon, Loader2Icon, XCircleIcon} from "lucide-react";
import {Progress} from "@/components/ui/progress.tsx";
import {BackupCreationProgressEvent, calculateOverallCreationPercentage, calculateOverallRestorePercentage, RestoreProgressEvent} from "@/backup/RestoreProgress.ts";
import {cn} from "@/lib/utils.ts";

const statusLabels: Record<RestoreProgressEvent["status"], string> = {
    QUEUED: "Wiederherstellung wartet…",
    VALIDATING: "Backup wird validiert…",
    CLEARING: "Vorhandener Datenbestand wird entfernt…",
    RESTORING: "Datenbestand wird wiederhergestellt…",
    SUCCEEDED: "Wiederherstellung erfolgreich abgeschlossen.",
    FAILED: "Wiederherstellung fehlgeschlagen. Alle Änderungen wurden zurückgerollt.",
};
const creationStatusLabels: Record<BackupCreationProgressEvent["status"], string> = {
    QUEUED: "Backup-Erstellung wartet…",
    EXPORTING: "Datenbestand wird gesichert…",
    FINALIZING: "Backup wird abgeschlossen…",
    SUCCEEDED: "Backup wurde erfolgreich erstellt.",
    FAILED: "Backup-Erstellung fehlgeschlagen.",
};

export function RestoreProgressPanel({event, embedded = false, operation = "restore"}: {event: RestoreProgressEvent | BackupCreationProgressEvent; embedded?: boolean; operation?: "restore" | "creation"}) {
    const creation = operation === "creation";
    const overallPercentage = creation ? calculateOverallCreationPercentage(event as BackupCreationProgressEvent) : calculateOverallRestorePercentage(event as RestoreProgressEvent);
    const label = creation
        ? creationStatusLabels[event.status as BackupCreationProgressEvent["status"]]
        : statusLabels[event.status as RestoreProgressEvent["status"]];

    return <section className={cn(
        "rounded-xl border bg-card p-4 shadow-sm",
        embedded && "mb-3 rounded-lg p-3 shadow-none",
        event.status === "SUCCEEDED" && "border-emerald-500/30 bg-emerald-500/5",
        event.status === "FAILED" && "border-destructive/30 bg-destructive/5",
        !event.finished && "border-primary/30 bg-primary/5"
    )} aria-live="polite" aria-atomic="true">
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                    {event.status === "SUCCEEDED" && <CheckCircle2Icon className="size-4 text-emerald-600"/>}
                    {event.status === "FAILED" && <XCircleIcon className="size-4 text-destructive"/>}
                    {!event.finished && <Loader2Icon className="size-4 animate-spin text-primary"/>}
                    <span>{label}</span>
                </div>
                <p className="mt-1 pl-6 text-xs text-muted-foreground">
                    {event.finished
                        ? event.failed ? "Der Vorgang wurde ohne fertiges Backup beendet." : creation ? "Das Backup wurde erfolgreich erstellt." : "Der Datenbestand wurde vollständig ersetzt."
                        : "Fortschrittsmeldungen werden automatisch aktualisiert."}
                </p>
            </div>
            <span className={cn(
                "text-sm font-semibold tabular-nums text-primary",
                event.status === "SUCCEEDED" && "text-emerald-600",
                event.status === "FAILED" && "text-destructive"
            )}>{event.status === "FAILED" ? "Fehler" : `${overallPercentage} %`}</span>
        </div>
        {!event.failed && <Progress value={overallPercentage} className="mt-3"
                                   aria-label={`Gesamtfortschritt: ${overallPercentage} Prozent`}/>}
        {event.error && <p role="alert" className="mt-2 text-xs text-destructive">{event.error}</p>}
    </section>;
}
